import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FullScreenModal,
  FullScreenModalProps,
} from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer } from '@/components/navigation/Footer';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { useSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { SelectedSuggestionEvidenceProvider } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.provider';
import { AIUserFeedbackButton } from '@/modules/aiUserFeedback/AIUserFeedbackButton';
import {
  DispositiveDiagramSyncProvider,
  useDispositiveDiagramSyncContext,
} from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { useDPFormRecipientTrackerContext } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.context';
import { DPFormRecipientTrackerProvider } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.provider';
import { DispositiveProvisionsModalDetailsType } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { SplitScreenLayoutWithDocuments } from '@/modules/documents/components/SplitScreenLayoutWithDocuments/SplitScreenLayoutWithDocuments';
import { useClientProfileDetailsContext } from '@/modules/entities/contexts/clientProfileDetails/clientProfileDetails.context';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useTestamentaryEntityDetailsContext } from '@/modules/entities/contexts/testamentaryEntityDetails/testamentaryEntityDetails.context';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
// eslint-disable-next-line luminary-rules/no-page-imports
import { useManageDispositionsContext } from '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/context/ManageDispositions.context';
// eslint-disable-next-line luminary-rules/no-page-imports
import { ManageDispositionPageSections } from '@/pages/ClientDetailsPage/ClientDetailsManageDispositionsPage/ManageDispositionPage.types';
import { UserFeedbackFeedbackCategory } from '@/types/schema';

import { DispositiveProvisionsModalBody } from './DispositiveProvisionsModalBody';

interface DispositiveProvisionsSplitScreenModalProps
  extends FullScreenModalProps {
  firstClientDeathId: string;
  currentNodeValue?: Decimal;
  isFromTemplateEditor?: boolean;
}

function DispositiveProvisionsSplitScreenModalInner({
  firstClientDeathId,
  currentNodeValue,
  isFromTemplateEditor,
  ...modalProps
}: DispositiveProvisionsSplitScreenModalProps) {
  const { householdId, primaryClients } = useHouseholdDetailsContext();
  const { entityId } = useEntityDetailsContext();
  const { entityId: testamentaryEntityId } =
    useTestamentaryEntityDetailsContext();
  const { clientProfileId } = useClientProfileDetailsContext();
  const { dispositiveProvisionsModalDetails: modalDetails, setActiveSection } =
    useManageDispositionsContext();
  const { handleRequestCloseOuterModal } = useDPFormRecipientTrackerContext();
  const { resetDiagramSyncState } = useDispositiveDiagramSyncContext();

  // Track the currently selected death order, defaulting to the initial prop
  const [activeDeathOrderId, setActiveDeathOrderId] =
    useState(firstClientDeathId);

  // Sync local state when prop changes (e.g., if parent updates the death order)
  useEffect(() => {
    setActiveDeathOrderId(firstClientDeathId);
  }, [firstClientDeathId]);

  const handleCloseModal = useCallback(() => {
    handleRequestCloseOuterModal(modalProps.onClose, () => {
      setActiveSection(ManageDispositionPageSections.TESTAMENTARY_ENTITIES);
      modalProps.onClose?.();
    });
  }, [handleRequestCloseOuterModal, modalProps, setActiveSection]);

  // Handler for after form submission - either switch death order or close modal.
  // This mirrors the pattern in EditEntitySplitScreenRightContent.handleAfterSubmit.
  const handleSwitchSectionOrCloseModal = useCallback(
    (afterSubmitSection?: EditEntitySection) => {
      if (afterSubmitSection) {
        // Extract the death order from the section and update state.
        // GRANTOR_1 = first primary client, GRANTOR_2 = second primary client.
        const newDeathOrderId =
          afterSubmitSection ===
          EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1
            ? primaryClients?.[0]?.id
            : primaryClients?.[1]?.id;

        if (newDeathOrderId) {
          // DispositiveDiagramSyncProvider persists at a higher level than the
          // keyed SplitScreenLayoutWithDocuments, so its state must be reset
          // before switching death orders or the next diagram reads the
          // previous death order's overlays (e.g. recipientDispositions).
          resetDiagramSyncState();
          setActiveDeathOrderId(newDeathOrderId);
        }
      } else {
        handleCloseModal();
      }
    },
    [handleCloseModal, primaryClients, resetDiagramSyncState]
  );

  // The reference to the footer of the modal, where the cancel/save buttons
  // will be portaled to.
  const footerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const showAIFeedbackButton = useMemo(() => {
    return (
      modalDetails &&
      modalDetails.type === DispositiveProvisionsModalDetailsType.ENTITY
    );
  }, [modalDetails]);

  const {
    documentPanelProps: {
      activeDocumentId,
      activeDocumentViewerTab,
      annotations,
      activeAnnotationIndex,
      searchQuery,
      searchLoading,
    },
    setDocumentPanelProps,
  } = useSelectedSuggestionEvidenceContext();

  const { suggestionDocumentIds } = useDispositiveProvisionSuggestionsContext();

  const overrideDocumentIds = useMemo(() => {
    return suggestionDocumentIds.length > 0 ? suggestionDocumentIds : undefined;
  }, [suggestionDocumentIds]);

  const targetId = entityId ?? testamentaryEntityId ?? clientProfileId ?? '';

  return (
    <FullScreenModal {...modalProps}>
      <FullScreenModalHouseholdChatHost>
        {({ householdChatPane }) => (
          <SplitScreenLayoutWithDocuments
            key={`${targetId}-${activeDeathOrderId}`}
            targetId={targetId}
            documentIds={overrideDocumentIds}
            initialPrimaryClientId={activeDeathOrderId}
            // Show the diagram for entities, client profiles, and testamentary entities
            showDiagram={!!targetId}
            householdId={householdId}
            header={<div ref={headerRef} />}
            rightContent={
              <DispositiveProvisionsModalBody
                variant="manageDispositions"
                nodeValue={currentNodeValue}
                headerRef={headerRef}
                footerRef={footerRef}
                onClose={() => modalProps.onClose?.()}
                onSwitchSectionAfterSubmit={handleSwitchSectionOrCloseModal}
                firstDeathClientId={activeDeathOrderId}
                isFromTemplateEditor={isFromTemplateEditor}
              />
            }
            rightPane={householdChatPane}
            footer={
              <Footer
                leftAction={
                  showAIFeedbackButton ? (
                    <AIUserFeedbackButton
                      feedbackCategory={
                        UserFeedbackFeedbackCategory.AiDpSuggestions
                      }
                    />
                  ) : undefined
                }
                rightAction={
                  // actions will be portaled here using the footerRef
                  <Stack
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                    direction="row"
                    ref={footerRef}
                  />
                }
              />
            }
            overrideActiveDocumentId={activeDocumentId}
            onUpdateActiveDocumentId={({
              documentId,
              shouldClearSearchResults,
            }) => {
              if (shouldClearSearchResults) {
                // Clear out the annotations and search-related props
                setDocumentPanelProps((prev) => ({
                  ...prev,
                  activeDocumentId: documentId,
                  searchQuery: '',
                  annotations: undefined,
                  activeAnnotationIndex: undefined,
                  searchLoading: false,
                }));
                return;
              }
              // Otherwise, only update the document id and tab
              setDocumentPanelProps((prev) => ({
                ...prev,
                activeDocumentId: documentId,
              }));
            }}
            overrideDocumentViewerActiveTab={activeDocumentViewerTab}
            overrideSearchQuery={searchQuery}
            overrideSearchLoading={searchLoading}
            overrideAnnotations={annotations}
            overrideActiveAnnotationIndex={activeAnnotationIndex}
            onUpdateDocumentViewerActiveTab={() => {
              // Unset the selected suggestion when the user switches between the document/summary views.
              setDocumentPanelProps({
                activeDocumentViewerTab: undefined,
                activeDocumentId: undefined,
                annotations: undefined,
                activeAnnotationIndex: undefined,
                searchQuery: '',
                searchLoading: false,
              });
            }}
          />
        )}
      </FullScreenModalHouseholdChatHost>
    </FullScreenModal>
  );
}

export function DispositiveProvisionsSplitScreenModal(
  props: DispositiveProvisionsSplitScreenModalProps
) {
  const { entityId } = useEntityDetailsContext();
  const { entityId: testamentaryEntityId } =
    useTestamentaryEntityDetailsContext();

  const dpSuggestionsTargetId = useMemo(() => {
    if (entityId) return entityId;
    if (testamentaryEntityId) return testamentaryEntityId;
    return '';
  }, [entityId, testamentaryEntityId]);

  return (
    <DPFormRecipientTrackerProvider>
      <DispositiveDiagramSyncProvider>
        <DispositiveProvisionSuggestionsProvider
          targetId={dpSuggestionsTargetId}
        >
          <SelectedSuggestionEvidenceProvider>
            <DispositiveProvisionsSplitScreenModalInner {...props} />
          </SelectedSuggestionEvidenceProvider>
        </DispositiveProvisionSuggestionsProvider>
      </DispositiveDiagramSyncProvider>
    </DPFormRecipientTrackerProvider>
  );
}
